import { Dispatch } from 'redux';
import { RootState } from '../configureStore';
import { SubscriptionService } from '../../services/SubscriptionService';
import { 
    setLoading, 
    setError, 
    setSubscriptions,
    setProducts,
    addSubscription,
    updateSubscription,
    removeSubscription
} from './reducer';
import { toFriendlyMessage } from '../../utils/errorUtils';

export const fetchSubscriptionsThunk = (userId: string) => {
    return async (dispatch: Dispatch) => {
        try {
            dispatch(setLoading(true));
            
            const [subs, products] = await Promise.all([
                SubscriptionService.getUserSubscriptions(),
                SubscriptionService.getProducts()
            ]);
            
            dispatch(setSubscriptions(subs));
            dispatch(setProducts(products));
        } catch (error) {
            dispatch(setError(toFriendlyMessage(error, 'Unable to load subscriptions')));
        }
    };
};

export const createSubscriptionThunk = (userId: string, productId: string, displayName: string) => {
    return async (dispatch: Dispatch) => {
        try {
            const subscriptionId = SubscriptionService.generateSubscriptionId();
            const newSub = await SubscriptionService.createSubscription(userId, subscriptionId, productId, displayName);
            
            dispatch(addSubscription(newSub));
        } catch (error) {
            const friendly = toFriendlyMessage(error, 'Unable to create subscription');
            dispatch(setError(friendly));
            // Re-throw with friendly message so the UI catch block gets a clean message
            const friendlyError = new Error(friendly);
            (friendlyError as any).cause = error;
            throw friendlyError;
        }
    };
};

export const cancelSubscriptionThunk = (subscriptionId: string) => {
    return async (dispatch: Dispatch) => {
        try {
            await SubscriptionService.cancelSubscription(subscriptionId);
            dispatch(removeSubscription(subscriptionId));
        } catch (error) {
            const friendly = toFriendlyMessage(error, 'Unable to cancel subscription');
            dispatch(setError(friendly));
            const friendlyError = new Error(friendly);
            (friendlyError as any).cause = error;
            throw friendlyError;
        }
    };
};

export const renameSubscriptionThunk = (subscriptionId: string, newName: string) => {
    return async (dispatch: Dispatch) => {
        try {
            await SubscriptionService.renameSubscription(subscriptionId, newName);
            const subs = await SubscriptionService.getUserSubscriptions('self');
            dispatch(setSubscriptions(subs));
        } catch (error) {
            const friendly = toFriendlyMessage(error, 'Unable to rename subscription');
            dispatch(setError(friendly));
            const friendlyError = new Error(friendly);
            (friendlyError as any).cause = error;
            throw friendlyError;
        }
    };
};

export const regenerateKeyThunk = (subscriptionId: string, keyType: 'primary' | 'secondary') => {
    return async (dispatch: Dispatch) => {
        try {
            await SubscriptionService.regenerateKey(subscriptionId, keyType);
            const subs = await SubscriptionService.getUserSubscriptions('self');
            dispatch(setSubscriptions(subs));
        } catch (error) {
            const friendly = toFriendlyMessage(error, `Unable to regenerate ${keyType} key`);
            dispatch(setError(friendly));
            const friendlyError = new Error(friendly);
            (friendlyError as any).cause = error;
            throw friendlyError;
        }
    };
};
